import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { showToast } from "../../utils/toast";
import { AdminAccessCodeView } from "./AdminAccessCode.view";

/**
 * Admin login step 2, the container. Session handoff, verification, toasts,
 * navigation.
 *
 * Everything visual lives in `AdminAccessCode.view.jsx`, which is pure, so the
 * state explorer can mount the real screen without any of this running.
 *
 * THREE DELIBERATE DIFFERENCES FROM THE PAGE THIS REPLACES
 * -------------------------------------------------------
 * 1. The stored session blob is read synchronously in the state initialiser
 *    rather than in an effect. The old page returned a full page spinner on the
 *    first render because `adminData` was still null, which is a blank screen
 *    for one frame on every visit. The effect below still owns the redirect and
 *    the toast for a missing or corrupt blob, unchanged.
 *
 * 2. `loading` is the submit's own state, not a full page spinner that unmounts
 *    the form and the code the user just typed.
 *
 * 3. A failed verification surfaces `result.error`. The old code read
 *    `result.message`, which `useAuth().adminLoginStep2` never sets on failure,
 *    so the server's actual reason was always discarded in favour of the
 *    hardcoded fallback.
 *
 * The commented-out `login(userData, access_token)` block is gone. It was dead:
 * `adminLoginVerify.fulfilled` in `store/slices/authSlice.js` already sets
 * `user` and `isAuthenticated`, and `setSession` is called inside the thunk.
 *
 * SECURITY, FLAGGED NOT CHANGED
 * -----------------------------
 * Step 1 writes the administrator's plaintext password into `sessionStorage`
 * alongside the email. Only the email is read here. Removing the password from
 * that blob is a security decision for a human, so it is left exactly as it was
 * and raised in the handoff.
 */

const STORAGE_KEY = "adminLoginData";
const CODE_LENGTH = 6;
const EMPTY_CODE = ["", "", "", "", "", ""];

function readStoredAdminData() {
  const stored = sessionStorage.getItem(STORAGE_KEY);
  if (!stored) return { status: "missing", data: null };
  try {
    return { status: "ok", data: JSON.parse(stored) };
  } catch (error) {
    return { status: "invalid", data: null };
  }
}

const AdminAccessCode = () => {
  const navigate = useNavigate();
  const { adminLoginStep2 } = useAuth();

  const [session] = useState(readStoredAdminData);
  const [digits, setDigits] = useState(EMPTY_CODE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const adminData = session.data;

  useEffect(() => {
    // Check if user came from the first step.
    if (session.status === "missing") {
      showToast.error("Please complete the first step", "error");
      navigate("/admin-login");
      return;
    }
    if (session.status === "invalid") {
      showToast.error("Invalid session data", "error");
      navigate("/admin-login");
    }
  }, [session, navigate]);

  const handleChange = (next) => {
    setDigits(next);
    if (error) setError("");
  };

  const accessCode = digits.join("");

  const validateAccessCode = () => {
    if (!accessCode.trim()) {
      setError("Access code is required");
      return false;
    }
    if (accessCode.length !== CODE_LENGTH) {
      setError("Access code must be 6 digits");
      return false;
    }
    if (!/^\d+$/.test(accessCode)) {
      setError("Access code must contain only numbers");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateAccessCode()) {
      showToast.error("Please enter a valid access code", "error");
      return;
    }

    if (!adminData) {
      showToast.error("Session expired. Please start over.", "error");
      navigate("/admin-login");
      return;
    }

    setLoading(true);

    try {
      const result = await adminLoginStep2(adminData.email, accessCode);

      if (result.success) {
        sessionStorage.removeItem(STORAGE_KEY);
        showToast.success(result.message || "Admin login successful!", "success");
        navigate("/admin-dashboard");
      } else {
        const reason = result.error || result.message || "Invalid access code";
        setError(reason);
        showToast.error(reason, "error");
      }
    } catch (error) {
      setError("Login failed. Please try again.");
      showToast.error("Login failed. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    navigate("/admin-login");
  };

  // The effect above is already navigating away; render nothing rather than
  // flashing a form that has no session behind it.
  if (!adminData) return null;

  return (
    <AdminAccessCodeView
      email={adminData.email}
      values={digits}
      onChange={handleChange}
      onSubmit={handleSubmit}
      onBack={handleBack}
      loading={loading}
      error={error}
    />
  );
};

export default AdminAccessCode;
